import { useState, useMemo } from 'react'
import type { SubTabId, TaskEffort, TaskExecutor } from '@/components/Configuracao/esforcoTimeTypes'
import { formatTotalHours, normalizeTaskHours } from '@/components/Configuracao/esforcoTimeUtils'

export function useEsforcoTimeTab() {
  const [activeSubTab, setActiveSubTab] = useState<SubTabId>('marketing')
  const [searchQuery, setSearchQuery] = useState('')

  // Sem seed — aba desativada na Rocket; lista começa vazia.
  const [marketingTasks, setMarketingTasks] = useState<TaskEffort[]>([])
  const [suporteTasks, setSuporteTasks] = useState<TaskEffort[]>([])
  const [projetoTasks, setProjetoTasks] = useState<TaskEffort[]>([])
  const [criacaoTasks, setCriacaoTasks] = useState<TaskEffort[]>([])

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [modalTask, setModalTask] = useState('')
  const [modalHours, setModalHours] = useState('01:00:00')
  const [modalExecutor, setModalExecutor] = useState<TaskExecutor>('Equipe')

  const taskCounts = {
    marketing: marketingTasks.length,
    suporte: suporteTasks.length,
    projeto: projetoTasks.length,
    criacao: criacaoTasks.length,
  }

  const currentTasks = useMemo(() => {
    switch (activeSubTab) {
      case 'marketing':
        return marketingTasks
      case 'suporte':
        return suporteTasks
      case 'projeto':
        return projetoTasks
      case 'criacao':
        return criacaoTasks
    }
  }, [activeSubTab, marketingTasks, suporteTasks, projetoTasks, criacaoTasks])

  const setCurrentTasks = (updated: TaskEffort[]) => {
    switch (activeSubTab) {
      case 'marketing':
        setMarketingTasks(updated)
        break
      case 'suporte':
        setSuporteTasks(updated)
        break
      case 'projeto':
        setProjetoTasks(updated)
        break
      case 'criacao':
        setCriacaoTasks(updated)
        break
    }
  }

  const filteredTasks = useMemo(() => {
    if (!searchQuery) return currentTasks
    const lower = searchQuery.toLowerCase().trim()
    return currentTasks.filter((t) => t.task.toLowerCase().includes(lower))
  }, [currentTasks, searchQuery])

  const totalHoursFormatted = useMemo(() => formatTotalHours(currentTasks), [currentTasks])

  const handleOpenAdd = () => {
    setEditId(null)
    setModalTask('')
    setModalHours('01:00:00')
    setModalExecutor('Equipe')
    setShowModal(true)
  }

  const handleOpenEdit = (task: TaskEffort) => {
    setEditId(task.id)
    setModalTask(task.task)
    setModalHours(task.hours)
    setModalExecutor(task.executor)
    setShowModal(true)
  }

  const handleDelete = (id: string) => {
    setCurrentTasks(currentTasks.filter((t) => t.id !== id))
  }

  const handleSaveModal = () => {
    if (!modalTask) return
    const validatedHours = normalizeTaskHours(modalHours)

    if (editId) {
      setCurrentTasks(
        currentTasks.map((t) =>
          t.id === editId ? { ...t, task: modalTask, hours: validatedHours, executor: modalExecutor } : t,
        ),
      )
    } else {
      setCurrentTasks([
        ...currentTasks,
        {
          id: Date.now().toString(),
          task: modalTask,
          hours: validatedHours,
          executor: modalExecutor,
        },
      ])
    }
    setShowModal(false)
  }

  const selectSubTab = (id: SubTabId) => {
    setActiveSubTab(id)
    setSearchQuery('')
  }

  return {
    activeSubTab,
    searchQuery,
    setSearchQuery,
    taskCounts,
    currentTasks,
    filteredTasks,
    totalHoursFormatted,
    showModal,
    setShowModal,
    editId,
    modalTask,
    setModalTask,
    modalHours,
    setModalHours,
    modalExecutor,
    setModalExecutor,
    handleOpenAdd,
    handleOpenEdit,
    handleDelete,
    handleSaveModal,
    selectSubTab,
  }
}
